import {createSlice, PayloadAction} from '@reduxjs/toolkit'
import {DistrictOption} from '../models/District'

interface DistrictState {
  items: Record<string, DistrictOption[]>
  loading: boolean
  /** Province codes for the in-flight batch request */
  loadingProvinceCodes: string[] | null
  error: string | null
  /** Province codes that the last error applied to */
  errorForCodes: string[] | null
}

const initialState: DistrictState = {
  items: {},
  loading: false,
  loadingProvinceCodes: null,
  error: null,
  errorForCodes: null,
}

const districtSlice = createSlice({
  name: 'zoneDistrict',
  initialState,
  reducers: {
    fetchDistrictsStart: (state, action: PayloadAction<string[]>) => {
      const codes = Array.from(
        new Set((action.payload || []).map(String).map((c) => c.trim()).filter(Boolean))
      )
      state.loading = true
      state.loadingProvinceCodes = codes.length > 0 ? codes : null
      state.error = null
      state.errorForCodes = null
    },
    fetchDistrictsSuccess: (
      state,
      action: PayloadAction<{byProvince: Record<string, DistrictOption[]>}>
    ) => {
      const {byProvince} = action.payload
      Object.entries(byProvince).forEach(([code, districts]) => {
        state.items[code] = districts
      })
      state.loading = false
      state.loadingProvinceCodes = null
      state.error = null
      state.errorForCodes = null
    },
    fetchDistrictsFailed: (
      state,
      action: PayloadAction<{requestedCodes: string[]; error: string}>
    ) => {
      const {requestedCodes, error} = action.payload
      state.loading = false
      state.loadingProvinceCodes = null
      state.error = error
      state.errorForCodes = requestedCodes.length > 0 ? requestedCodes : null
    },
    clearDistricts: (state) => {
      state.items = {}
      state.loading = false
      state.loadingProvinceCodes = null
      state.error = null
      state.errorForCodes = null
    },
  },
})

export const districtActions = districtSlice.actions
export const districtReducer = districtSlice.reducer

export const selectZoneDistrictState = (state: any): DistrictState => state.zoneDistrict

export const selectDistrictsByProvince = (state: any, provinceId?: string): DistrictOption[] => {
  if (!provinceId) {
    return []
  }
  const slice = selectZoneDistrictState(state)
  return slice?.items?.[provinceId] || []
}

export const selectDistrictLoadingForProvince = (state: any, provinceId?: string): boolean => {
  if (!provinceId) {
    return false
  }
  const slice = selectZoneDistrictState(state)
  return Boolean(slice.loading && slice.loadingProvinceCodes?.includes(provinceId))
}

export const selectDistrictErrorForProvince = (state: any, provinceId?: string): string | null => {
  if (!provinceId) {
    return null
  }
  const slice = selectZoneDistrictState(state)
  if (!slice.error || !slice.errorForCodes?.includes(provinceId)) {
    return null
  }
  return slice.error
}

export type {DistrictState}
